import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  HostBinding,
  Inject,
  Input,
  NgZone,
  OnDestroy,
  Output,
} from '@angular/core';
import {DOCUMENT} from '@angular/common';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';

export type TurnstileAppearance = 'always' | 'execute' | 'interaction-only';
export type TurnstileExecution = 'render' | 'execute';
export type TurnstileTheme = 'auto' | 'light' | 'dark';
export type TurnstileSize = 'normal' | 'flexible' | 'compact';

interface TurnstileRenderOptions {
  sitekey: string;
  appearance?: TurnstileAppearance;
  execution?: TurnstileExecution;
  action?: string;
  cData?: string;
  theme?: TurnstileTheme;
  language?: string;
  size?: TurnstileSize;
  tabindex?: number;
  retry?: 'auto' | 'never';
  callback?: (token: string) => void;
  'error-callback'?: (code: string) => void;
  'expired-callback'?: () => void;
  'timeout-callback'?: () => void;
  'before-interactive-callback'?: () => void;
  'after-interactive-callback'?: () => void;
}

interface TurnstileApi {
  render(el: HTMLElement, options: TurnstileRenderOptions): string;
  execute(widget: string | HTMLElement, options?: Partial<TurnstileRenderOptions>): void;
  reset(widget?: string): void;
  remove(widget?: string): void;
}

const SCRIPT_ID = 'cf-turnstile-script';
const ONLOAD_CALLBACK = 'onloadTurnstileCallback';
const SCRIPT_SRC =
  `https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit&onload=${ONLOAD_CALLBACK}`;

// Shared across all instances: the Cloudflare script is injected once.
let scriptPromise: Promise<void> | null = null;

function turnstileApi(): TurnstileApi | undefined {
  return (window as unknown as {turnstile?: TurnstileApi}).turnstile;
}

function loadTurnstileScript(doc: Document): Promise<void> {
  if (turnstileApi()) {
    return Promise.resolve();
  }
  if (scriptPromise) {
    return scriptPromise;
  }
  scriptPromise = new Promise<void>((resolve, reject) => {
    (window as unknown as Record<string, unknown>)[ONLOAD_CALLBACK] = () => resolve();
    if (doc.getElementById(SCRIPT_ID)) {
      return;
    }
    const script = doc.createElement('script');
    script.id = SCRIPT_ID;
    script.src = SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onerror = () => {
      // Remove the failed node and clear the promise so a later mount re-injects
      // instead of finding a dead <script id=cf-turnstile-script> and hanging on
      // a promise that never settles.
      script.remove();
      scriptPromise = null;
      reject(new Error('turnstile-script-failed'));
    };
    doc.head.appendChild(script);
  });
  return scriptPromise;
}

/**
 * Cloudflare Turnstile wrapper. Drop-in replacement for the ng-recaptcha
 * `<re-captcha>` invisible widget: integrates as a reactive-form control
 * (`ControlValueAccessor`), exposes `execute()`/`reset()` and the
 * `resolved`/`errored`/`interactive` outputs.
 *
 * ngx-turnstile is not usable here (it requires Angular >= 16); this wrapper
 * talks to the raw `window.turnstile` API and works on Angular 13+.
 *
 * The b2c dashboard widget is **Managed**: Cloudflare presents an interactive
 * challenge to visitors it cannot clear passively. `appearance:
 * 'interaction-only'` is therefore the only correct default — the widget stays
 * hidden for everyone else, and `execution: 'execute'` still mints the token on
 * submit. Do NOT pass `appearance: 'execute'`: it pins the host to
 * `visibility: hidden` (see hostVisibility), so a real challenge is rendered
 * inside an invisible element and that cohort has no way to solve it. Shipping
 * that combination is what caused the 2026-08-17 login incident.
 *
 * A host that shows the challenge must also stop covering it: subscribe to
 * `interactive` and drop any loading overlay while it emits true.
 */
@Component({
  selector: 'app-turnstile',
  template: '',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TurnstileComponent),
      multi: true,
    },
  ],
})
export class TurnstileComponent implements AfterViewInit, OnDestroy, ControlValueAccessor {
  @Input() siteKey!: string;
  @Input() appearance: TurnstileAppearance = 'interaction-only';
  @Input() execution: TurnstileExecution = 'execute';
  @Input() language = 'auto';
  @Input() theme: TurnstileTheme = 'auto';
  @Input() size: TurnstileSize = 'normal';
  @Input() retry: 'auto' | 'never' = 'auto';
  @Input() action?: string;
  @Input() cData?: string;
  @Input() tabIndex?: number;

  @Output() resolved = new EventEmitter<string | null>();
  @Output() errored = new EventEmitter<string>();
  // True while Cloudflare is showing a challenge the visitor has to solve. The
  // host must not cover the widget with a loading overlay during that window.
  @Output() interactive = new EventEmitter<boolean>();
  // Page-load failure of the Cloudflare script — telemetry only, so the host
  // can log/measure it WITHOUT alarming the user before they act. A failure at
  // submit time comes through `errored` instead.
  @Output() loadError = new EventEmitter<string>();

  private widgetId?: string;
  private pendingExecute = false;
  private executing = false;
  private hasToken = false;
  private disabled = false;
  private destroyed = false;
  private loadFailed = false;

  private onChange: (value: string | null) => void = () => undefined;
  private onTouched: () => void = () => undefined;

  constructor(
    private readonly elementRef: ElementRef<HTMLElement>,
    private readonly zone: NgZone,
    @Inject(DOCUMENT) private readonly document: Document,
  ) {}

  // 'execute' is the one appearance that hides the host outright, which is why
  // it must not be used on a Managed widget: it swallows a real challenge. Every
  // other appearance lets Cloudflare decide what to show.
  @HostBinding('style.visibility')
  get hostVisibility(): string {
    return this.appearance === 'execute' ? 'hidden' : 'visible';
  }

  ngAfterViewInit(): void {
    loadTurnstileScript(this.document)
      .then(() => {
        if (this.destroyed) {
          return;
        }
        this.zone.runOutsideAngular(() => this.renderWidget());
      })
      .catch(() => {
        if (!this.destroyed) {
          this.zone.run(() => this.markLoadUnavailable('script-load-error'));
        }
      });
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    if (this.widgetId) {
      turnstileApi()?.remove(this.widgetId);
      this.widgetId = undefined;
    }
  }

  execute(): void {
    if (this.disabled || this.executing || this.pendingExecute || this.hasToken) {
      return;
    }
    if (this.loadFailed) {
      // The script never loaded, so the widget will never render and the queue
      // below would never drain. Fail fast so the caller's loading state
      // resolves instead of hanging on an infinite spinner.
      this.onError('script-load-error');
      return;
    }
    const api = turnstileApi();
    if (this.widgetId && api) {
      this.executing = true;
      api.execute(this.widgetId, {appearance: this.appearance});
    } else {
      // Widget not rendered yet: run as soon as it is.
      this.pendingExecute = true;
    }
  }

  reset(): void {
    // Clear every flag before touching the API: a widget that never rendered
    // would otherwise keep hasToken set, and execute() no-ops on it forever,
    // leaving the caller on a spinner that nothing resolves.
    this.pendingExecute = false;
    this.executing = false;
    const hadToken = this.hasToken;
    this.hasToken = false;

    const api = turnstileApi();
    if (this.widgetId && api) {
      api.reset(this.widgetId);
    }
    if (hadToken) {
      this.zone.run(() => {
        this.resolved.emit(null);
        this.onChange(null);
      });
    }
  }

  writeValue(value: string | null): void {
    // View -> model only. An external reset (form.reset()/setValue(''))
    // re-arms the widget so the next execute() mints a fresh token.
    if (!value && this.hasToken) {
      this.reset();
    }
  }

  registerOnChange(fn: (value: string | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  private renderWidget(): void {
    if (this.destroyed) {
      return;
    }
    const api = turnstileApi();
    if (!api) {
      this.zone.run(() => this.markLoadUnavailable('script-load-error'));
      return;
    }
    const widgetId = api.render(this.elementRef.nativeElement, {
      sitekey: this.siteKey,
      appearance: this.appearance,
      execution: this.execution,
      action: this.action,
      cData: this.cData,
      theme: this.theme,
      language: this.language,
      size: this.size,
      tabindex: this.tabIndex,
      retry: this.retry,
      callback: (token: string) => this.zone.run(() => this.onToken(token)),
      'error-callback': (code: string) => this.zone.run(() => this.onError(code)),
      'expired-callback': () => this.zone.run(() => this.onExpired()),
      'timeout-callback': () => this.zone.run(() => this.onError('timeout')),
      'before-interactive-callback': () => this.zone.run(() => this.interactive.emit(true)),
      'after-interactive-callback': () => this.zone.run(() => this.interactive.emit(false)),
    });
    this.widgetId = widgetId;
    if (this.pendingExecute) {
      this.pendingExecute = false;
      if (!this.disabled) {
        this.executing = true;
        api.execute(widgetId, {appearance: this.appearance});
      }
    }
  }

  private onToken(token: string): void {
    this.hasToken = true;
    this.executing = false;
    this.onChange(token);
    this.onTouched();
    this.resolved.emit(token);
  }

  private onError(code: string): void {
    this.executing = false;
    // Keep hasToken in sync with the nulled model.
    this.hasToken = false;
    this.onChange(null);
    this.errored.emit(code);
  }

  private onExpired(): void {
    this.reset();
  }

  private markLoadUnavailable(code: string): void {
    // The Cloudflare script failed to load (or window.turnstile is absent), so
    // the widget will never render. Report it as telemetry (loadError), not as a
    // user-facing error, and — if a submit is already waiting on the widget —
    // fail that submit fast so the caller's loading state resolves.
    this.loadFailed = true;
    this.loadError.emit(code);
    if (this.pendingExecute) {
      this.pendingExecute = false;
      this.onError(code);
    }
  }
}
